import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from 'react';
import { createCustomPinMarker } from '../compo/pin';
import { PropertySidebar } from '../compo/card';
import { fetchProperties, deleteProperty, updateProperty } from '../utils/api';
import { waitForKakaoMapAPI } from '../utils/kakaoMapLoader';
import { applyFilters } from '../utils/propertyFilters';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { geocodeAddress } from '../utils/propertyGeo';
import html2canvas from 'html2canvas';

const MIN_MAP_HEIGHT = 360;
const DEFAULT_CENTER = { lat: 35.157739, lng: 129.059030 };

// 상권별 색상 팔레트 (선명하고 비비드한 색상으로 구분)
const AREA_COLORS = [
  { stroke: '#FF4500', fill: '#FF4500' },      // 주황색 (OrangeRed)
  { stroke: '#00CED1', fill: '#00CED1' },      // 청록색 (DarkTurquoise)
  { stroke: '#1E90FF', fill: '#1E90FF' },      // 하늘색 (DodgerBlue)
  { stroke: '#FF6347', fill: '#FF6347' },      // 연어색 (Tomato)
  { stroke: '#00FA9A', fill: '#00FA9A' },      // 민트색 (MediumSpringGreen)
  { stroke: '#FFD700', fill: '#FFD700' },      // 노란색 (Gold)
  { stroke: '#9370DB', fill: '#9370DB' },      // 보라색 (MediumPurple)
  { stroke: '#4169E1', fill: '#4169E1' },      // 파란색 (RoyalBlue)
  { stroke: '#DC143C', fill: '#DC143C' },      // 빨간색 (Crimson)
  { stroke: '#32CD32', fill: '#32CD32' },      // 초록색 (LimeGreen)
  { stroke: '#FF8C00', fill: '#FF8C00' },      // 금색 (DarkOrange)
  { stroke: '#FF1493', fill: '#FF1493' },      // 핑크색 (DeepPink)
  { stroke: '#00BFFF', fill: '#00BFFF' },      // 밝은 파란색 (DeepSkyBlue)
  { stroke: '#FF6600', fill: '#FF6600' },      // 오렌지색 (밝은 주황)
  { stroke: '#FF0000', fill: '#FF0000' },      // 빨간색 (Red)
  { stroke: '#228B22', fill: '#228B22' },      // 초록색 (ForestGreen)
  { stroke: '#8A2BE2', fill: '#8A2BE2' },      // 보라색 (BlueViolet)
  { stroke: '#0000FF', fill: '#0000FF' },      // 파란색 (Blue)
  { stroke: '#CD853F', fill: '#CD853F' },      // 갈색 (Peru)
  { stroke: '#20B2AA', fill: '#20B2AA' },      // 청록색 (LightSeaGreen)
];

// 커스텀 다각형 데이터 (하드코딩)
// 형식: { id: number, name: string, points: [{ lat: number, lng: number }], color: string }
const DEFAULT_CUSTOM_POLYGONS = [
  {
    id: 1,
    name: '부전시장',
    points: [
      { lat: 35.15793631921133, lng: 129.05909122921736 },
      { lat: 35.16094212625, lng: 129.06144948630015 },
      { lat: 35.160959827248774, lng: 129.05669842825804 }
    ],
    color: '#FF4500'
  },
  {
    id: 2,
    name: '영광도서',
    points: [
      { lat: 35.160680136258705, lng: 129.05472714225732 },
      { lat: 35.16129228568127, lng: 129.05609228440306 },
      { lat: 35.15789275804768, lng: 129.0588706702717 },
      { lat: 35.15782703227602, lng: 129.05478155761148 }
    ],
    color: '#00CED1'
  },
  {
    id: 3,
    name: '혜도인근',
    points: [
      { lat: 35.16127969120881, lng: 129.06213287644545 },
      { lat: 35.15787168293703, lng: 129.06222788725583 },
      { lat: 35.157820938567085, lng: 129.05938459235333 }
    ],
    color: '#1E90FF'
  },
  {
    id: 4,
    name: '진경찰서',
    points: [
      { lat: 35.16145200061314, lng: 129.05012670373998 },
      { lat: 35.16101944014984, lng: 129.05012681886058 },
      { lat: 35.160225127931845, lng: 129.05100121003792 },
      { lat: 35.15782555607526, lng: 129.05247718137608 },
      { lat: 35.15786023438066, lng: 129.05468363545043 },
      { lat: 35.160739803471856, lng: 129.05466280279694 },
      { lat: 35.161215930650066, lng: 129.05421938106448 },
      { lat: 35.16170166126797, lng: 129.05400664060787 },
      { lat: 35.161217735197575, lng: 129.05251853391056 }
    ],
    color: '#32CD32'
  },
  {
    id: 5,
    name: '전리단길',
    points: [
      { lat: 35.16123955084858, lng: 129.06237327952613 },
      { lat: 35.161446655716496, lng: 129.06264736049422 },
      { lat: 35.160525873745144, lng: 129.06512055009784 },
      { lat: 35.157880703423196, lng: 129.06513596722928 },
      { lat: 35.15788117869734, lng: 129.06246404715725 }
    ],
    color: '#FFD700'
  },
  {
    id: 6,
    name: '놀이터 시장',
    points: [
      { lat: 35.160340769186305, lng: 129.06553834376825 },
      { lat: 35.15784844992317, lng: 129.06557407251302 },
      { lat: 35.157858131296415, lng: 129.06817492231312 },
      { lat: 35.158974516905865, lng: 129.06797275419618 },
      { lat: 35.15964087184149, lng: 129.06721052493148 }
    ],
    color: '#00FA9A'
  },
  {
    id: 7,
    name: '4번가술집',
    points: [
      { lat: 35.15772041721717, lng: 129.06555163198814 },
      { lat: 35.15775923379133, lng: 129.06816144517506 },
      { lat: 35.15694286524721, lng: 129.06820935543118 },
      { lat: 35.15458045622652, lng: 129.06828944266115 },
      { lat: 35.15457442864619, lng: 129.06560649548732 }
    ],
    color: '#9370DB'
  },
  {
    id: 8,
    name: '4번가카페',
    points: [
      { lat: 35.14980286971079, lng: 129.06560106124124 },
      { lat: 35.14972079970317, lng: 129.06618598436273 },
      { lat: 35.15049949626927, lng: 129.0683452219575 },
      { lat: 35.15446476497648, lng: 129.06833588999908 },
      { lat: 35.15450255386929, lng: 129.065593705494 }
    ],
    color: '#4169E1'
  },
  {
    id: 9,
    name: 'NC,카페거리',
    points: [
      { lat: 35.157710732049004, lng: 129.06241859451129 },
      { lat: 35.15771053851677, lng: 129.06507405664806 },
      { lat: 35.15006730490015, lng: 129.0651551527354 },
      { lat: 35.15054677020617, lng: 129.06240231772762 },
      { lat: 35.15223178026721, lng: 129.06241193739112 }
    ],
    color: '#DC143C'
  },
  {
    id: 10,
    name: '배대포',
    points: [
      { lat: 35.157647527226224, lng: 129.0622935523581 },
      { lat: 35.15760352618513, lng: 129.05931875929687 },
      { lat: 35.15539982267363, lng: 129.05934001183618 },
      { lat: 35.15541726965048, lng: 129.06228662232996 }
    ],
    color: '#FF6347'
  },
  {
    id: 11,
    name: '쥬디스,학원가',
    points: [
      { lat: 35.15533943988699, lng: 129.05931380102132 },
      { lat: 35.15534745960667, lng: 129.06228485989809 },
      { lat: 35.152265081612605, lng: 129.06230854146108 },
      { lat: 35.15078367968022, lng: 129.06224920296677 },
      { lat: 35.15081464989421, lng: 129.05936981274087 }
    ],
    color: '#FF8C00'
  },
  {
    id: 12,
    name: '롯데,서면시장',
    points: [
      { lat: 35.15742249934503, lng: 129.0527304051784 },
      { lat: 35.15747465721858, lng: 129.05894791534357 },
      { lat: 35.154529691610556, lng: 129.0589724397813 },
      { lat: 35.154535187366925, lng: 129.05559850618974 },
      { lat: 35.15412291252147, lng: 129.05440309751737 }
    ],
    color: '#FF1493'
  },
  {
    id: 13,
    name: '일번가,복개로',
    points: [
      { lat: 35.1540193228879, lng: 129.05440049241585 },
      { lat: 35.15439190047244, lng: 129.05554552402361 },
      { lat: 35.15442413003213, lng: 129.05895332027896 },
      { lat: 35.15082695312732, lng: 129.05904370320832 },
      { lat: 35.15067469339929, lng: 129.05766287109884 },
      { lat: 35.1511148484656, lng: 129.0561543161132 },
      { lat: 35.1521302197168, lng: 129.05500035127076 }
    ],
    color: '#00BFFF'
  }
];

// 상권명 정규화 함수 (공백 제거, 대소문자 통일 등)
function normalizeAreaName(name) {
  if (!name) return '';
  return name.trim().replace(/\s+/g, '').toLowerCase();
}

// 상권명 매핑 테이블 (매물 데이터의 상권명 -> 다각형의 상권명)
// 예: "부전시장 " -> "부전시장", "부전 시장" -> "부전시장"
const AREA_NAME_MAPPING = {
  // 다각형 이름들을 기준으로 매핑
  '부전시장': '부전시장',
  '영광도서': '영광도서',
  '혜도인근': '혜도인근',
  '진경찰서': '진경찰서',
  '전리단길': '전리단길',
  '놀이터시장': '놀이터 시장',
  '놀이터 시장': '놀이터 시장',
  '4번가술집': '4번가술집',
  '4번가카페': '4번가카페',
  'nc카페거리': 'NC,카페거리',
  'nc,카페거리': 'NC,카페거리',
  'nc 카페거리': 'NC,카페거리',
  '배대포': '배대포',
  '쥬디스학원가': '쥬디스,학원가',
  '쥬디스,학원가': '쥬디스,학원가',
  '쥬디스 학원가': '쥬디스,학원가',
  '롯데서면시장': '롯데,서면시장',
  '롯데,서면시장': '롯데,서면시장',
  '롯데 서면시장': '롯데,서면시장',
  '일번가복개로': '일번가,복개로',
  '일번가,복개로': '일번가,복개로',
  '일번가 복개로': '일번가,복개로',
};

// 정규화된 상권명으로 매핑 찾기
function findMappedAreaName(areaName) {
  if (!areaName) return null;
  
  // 정규화된 이름으로 매핑 찾기
  const normalized = normalizeAreaName(areaName);
  
  // 직접 매핑 확인
  for (const [key, value] of Object.entries(AREA_NAME_MAPPING)) {
    if (normalizeAreaName(key) === normalized) {
      return value;
    }
  }
  
  // 부분 매칭 시도 (예: "부전시장 " -> "부전시장")
  for (const [key, value] of Object.entries(AREA_NAME_MAPPING)) {
    if (normalized.includes(normalizeAreaName(key)) || normalizeAreaName(key).includes(normalized)) {
      return value;
    }
  }
  
  return null;
}

// 상권별 색상 매핑을 저장 (일관성 유지)
const areaColorMap = new Map();

/**
 * 상권명에 따라 색상을 할당하는 함수
 * 상권 목록을 정렬하여 인덱스로 할당하여 충돌 방지
 */
function getAreaColor(areaName, allAreas) {
  // 먼저 매핑된 상권명 찾기
  const mappedName = findMappedAreaName(areaName);
  const finalAreaName = mappedName || areaName;
  
  // 상권 목록이 제공되면 정렬하여 인덱스로 할당
  if (allAreas && allAreas.length > 0) {
    // 정규화된 이름으로 비교
    const normalizedAreas = allAreas.map(a => ({
      original: a,
      normalized: normalizeAreaName(a)
    }));
    
    const normalizedTarget = normalizeAreaName(finalAreaName);
    const matchedArea = normalizedAreas.find(a => 
      normalizeAreaName(a.original) === normalizedTarget ||
      findMappedAreaName(a.original) === finalAreaName
    );
    
    if (matchedArea) {
      const sortedAreas = [...allAreas].sort();
      const index = sortedAreas.indexOf(matchedArea.original);
      if (index >= 0) {
        const color = AREA_COLORS[index % AREA_COLORS.length];
        areaColorMap.set(areaName, color);
        areaColorMap.set(finalAreaName, color);
        return color;
      }
    }
  }
  
  // 기존 매핑이 있으면 사용
  if (areaColorMap.has(areaName)) {
    return areaColorMap.get(areaName);
  }
  if (areaColorMap.has(finalAreaName)) {
    return areaColorMap.get(finalAreaName);
  }
  
  // 새로운 상권이면 더 나은 해시 함수 사용
  let hash = 0;
  const nameToHash = finalAreaName;
  for (let i = 0; i < nameToHash.length; i++) {
    const char = nameToHash.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32비트 정수로 변환
  }
  const index = Math.abs(hash) % AREA_COLORS.length;
  const color = AREA_COLORS[index];
  
  // 매핑 저장 (원본 이름과 매핑된 이름 모두)
  areaColorMap.set(areaName, color);
  areaColorMap.set(finalAreaName, color);
  return color;
}

const MainMap = forwardRef(function MainMap({ filters = {}, consultations = [], availableHeight, onFocusMap, onAddInterestedProperty, isScreenshotMode = false, onEnterScreenshotMode, onExitScreenshotMode, onSwitchToTab }, ref) {
  const { role, loading: authLoading, isAuthenticated } = useAuth();
  const isVisitor = role === ROLES.VISITOR;
  const isGuest = role === ROLES.GUEST;
  
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]); // [{ marker, property }] 형태로 저장
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [propertyData, setPropertyData] = useState([]);
  const [mapCenter] = useState(DEFAULT_CENTER); // resetMap에서 사용
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarkers, setShowMarkers] = useState(!isVisitor); // visitor는 기본적으로 매물 핀 비활성화
  const [showPolygons, setShowPolygons] = useState(true); // 다각형 표시 여부
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false); // 퀵메뉴 열림 여부
  const [focusedPropertyId, setFocusedPropertyId] = useState(null); // 하이라이트할 매물 ID
  const [propertiesWithoutCoordinates, setPropertiesWithoutCoordinates] = useState([]); // 좌표가 없는 매물 목록
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false); // 경고 모달 열림 여부
  
  // 하드코딩된 다각형만 사용
  const customPolygons = DEFAULT_CUSTOM_POLYGONS;
  const customPolygonPolygonsRef = useRef([]); // 표시된 다각형들
  const customPolygonLabelsRef = useRef([]); // 다각형 라벨들

  // 백엔드에서 매물 데이터 가져오기 (로그인한 경우에만)
  useEffect(() => {
    // 인증 상태 확인 중이면 대기
    if (authLoading) {
      return;
    }
    
    // 로그인하지 않았거나 visitor면 매물 로드하지 않음
    if (!isAuthenticated || isVisitor) {
      setIsLoading(false);
      setPropertyData([]);
      return;
    }
    
    async function loadProperties() {
      try {
        setIsLoading(true);
        setError(null);
        // 지도 표시용이므로 좌표가 있는 매물만 불러오기 (비용 절감)
        const data = await fetchProperties(role, true); // hasCoordinates=true로 좌표가 있는 매물만
        setPropertyData(data);
        
        // 좌표가 없는 매물도 별도로 가져오기 (자동 지오코딩 및 경고 표시용)
        const allProperties = await fetchProperties(role, false); // 전체 매물 가져오기
        const missingCoordinates = allProperties.filter(p => p.주소 && (!p.lat || !p.lng));
        
        // 좌표가 없는 매물들에 대해 자동 지오코딩 시도
        if (missingCoordinates.length > 0) {
          console.log(`📍 좌표가 없는 매물 ${missingCoordinates.length}개 발견, 자동 지오코딩 시도 중...`);
          
          const geocodingResults = [];
          for (const property of missingCoordinates) {
            try {
              const coords = await geocodeAddress(property.주소);
              if (coords && coords.lat && coords.lng) {
                // 지오코딩 성공 - 매물 업데이트
                try {
                  await updateProperty(property.id, {
                    lat: coords.lat,
                    lng: coords.lng
                  });
                  console.log(`✅ 지오코딩 성공 및 저장: ${property.상호 || property.name} (${property.주소})`);
                  geocodingResults.push({ property, success: true });
                } catch (updateError) {
                  console.error(`❌ 매물 업데이트 실패: ${property.상호 || property.name}`, updateError);
                  geocodingResults.push({ property, success: false, error: '저장 실패' });
                }
              } else {
                // 지오코딩 실패
                console.warn(`⚠️ 지오코딩 실패: ${property.상호 || property.name} (${property.주소})`);
                geocodingResults.push({ property, success: false, error: '지오코딩 실패' });
              }
            } catch (geocodeError) {
              console.error(`❌ 지오코딩 오류: ${property.상호 || property.name}`, geocodeError);
              geocodingResults.push({ property, success: false, error: '지오코딩 오류' });
            }
          }
          
          // 실패한 매물들만 경고 목록에 표시
          const failedProperties = geocodingResults
            .filter(r => !r.success)
            .map(r => r.property);
          setPropertiesWithoutCoordinates(failedProperties);
          
          const successCount = geocodingResults.filter(r => r.success).length;
          const failCount = failedProperties.length;
          
          if (successCount > 0) {
            console.log(`✅ ${successCount}개 매물 지오코딩 성공 및 저장 완료`);
            // 성공한 매물이 있으면 매물 목록 새로고침 (지도에 표시하기 위해)
            const refreshedData = await fetchProperties(role, true);
            setPropertyData(refreshedData);
          }
          
          if (failCount > 0) {
            console.warn(`⚠️ 지오코딩 실패한 매물 ${failCount}개:`, failedProperties.map(p => `${p.상호 || p.name} (${p.주소})`));
          }
        } else {
          setPropertiesWithoutCoordinates([]);
        }
      } catch (err) {
        console.error('매물 데이터 로드 실패:', err);
        setError(err.message || '매물 데이터를 불러오는데 실패했습니다.');
        // 에러 발생 시 빈 배열로 설정하여 앱이 계속 작동하도록 함
        setPropertyData([]);
        setPropertiesWithoutCoordinates([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadProperties();
  }, [authLoading, isAuthenticated, isVisitor, role]);


  // 스크린샷 모드일 때 지도 크기 조정
  useEffect(() => {
    if (isScreenshotMode && map.current) {
      setTimeout(() => {
        if (map.current && typeof map.current.relayout === 'function') {
          map.current.relayout();
        }
      }, 300);
    }
  }, [isScreenshotMode]);

  // 지도 초기화 (한 번만 실행)
  useEffect(() => {
    let isMounted = true;

    async function setupMap() {
      try {
        // 1. 지도 컨테이너가 준비될 때까지 기다림
        while (!mapContainer.current) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // 2. 카카오맵 API가 준비될 때까지 기다림
        await waitForKakaoMapAPI();

        if (!isMounted) return;

        // 3. 지도 생성 (서면역 중심)
        const seomyeonPosition = new window.kakao.maps.LatLng(mapCenter.lat, mapCenter.lng);
        const seomyeonBounds = new window.kakao.maps.LatLngBounds(
          new window.kakao.maps.LatLng(35.148, 129.045),
          new window.kakao.maps.LatLng(35.168, 129.075)
        );

        map.current = new window.kakao.maps.Map(mapContainer.current, {
          center: seomyeonPosition,
          level: 3,
          draggable: true,
          scrollwheel: true,
          disableDoubleClick: false,
          disableDoubleClickZoom: false
        });

        // 지도 크기 조정 및 중심 재설정 (컨테이너 크기 확정 후)
        setTimeout(() => {
          if (map.current) {
            map.current.relayout();
            // relayout 후 중심을 다시 설정하여 정확히 중앙에 위치하도록
            map.current.setCenter(seomyeonPosition);
          }
        }, 100);

        // 줌 레벨 제한 (레벨이 낮을수록 더 확대됨)
        map.current.setMinLevel(1);
        map.current.setMaxLevel(6);

        // 드래그 제한: 서면 경계를 벗어나면 자동으로 돌아오기
        window.kakao.maps.event.addListener(map.current, 'dragend', function() {
          const center = map.current.getCenter();
          const sw = seomyeonBounds.getSouthWest();
          const ne = seomyeonBounds.getNorthEast();
          
          if (center.getLat() < sw.getLat() - 0.02 || center.getLat() > ne.getLat() + 0.02 ||
              center.getLng() < sw.getLng() - 0.02 || center.getLng() > ne.getLng() + 0.02) {
            map.current.panTo(seomyeonPosition);
          }
        });
      } catch (error) {
        console.error('지도 초기화 오류:', error);
        setError('카카오맵을 불러오는데 실패했습니다. 페이지를 새로고침해주세요.');
      }
    }

    setupMap();

    // cleanup 함수
    return () => {
      isMounted = false;
      markersRef.current.forEach(({ marker }) => {
        if (marker.setMap) {
          marker.setMap(null);
        } else if (marker.content) {
          marker.setMap(null);
        }
      });
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 지도는 한 번만 초기화 (mapCenter는 의도적으로 dependency에서 제외)

  // 자동 중심 조정 로직 제거
  // 원래는 모든 매물들의 다각형을 만들 때 그 정 가운데를 찾기 위한 방식이었지만,
  // 이제는 매물 다각형도 고정된 상황이므로 자동 조정이 필요 없음
  // 사용자가 직접 지도를 조작하거나 focusOnLocation으로 이동한 위치를 유지하는 것이 더 자연스러움

  // 외부에서 지도 이동을 제어할 수 있도록 함수 노출
  const focusOnLocation = (lat, lng, propertyId = null) => {
    // 좌표 유효성 검사
    const numLat = Number(lat);
    const numLng = Number(lng);
    
    if (isNaN(numLat) || isNaN(numLng)) {
      console.error('❌ focusOnLocation: 유효하지 않은 좌표:', { lat, lng, numLat, numLng });
      return;
    }
    
    console.log('focusOnLocation 호출:', { lat, lng, numLat, numLng, propertyId, mapExists: !!map.current, kakaoExists: !!window.kakao?.maps });
    
    if (!map.current || !window.kakao?.maps) {
      console.log('지도가 준비되지 않음, 재시도 예정');
      setTimeout(() => {
        if (map.current && window.kakao?.maps) {
    const targetPosition = new window.kakao.maps.LatLng(numLat, numLng);
    console.log('지도 이동 시도 (재시도):', targetPosition, '좌표:', { lat: numLat, lng: numLng });
    
    // setCenter를 사용하여 즉시 이동
          map.current.setCenter(targetPosition);
          map.current.setLevel(1); // 최대 확대
          if (propertyId) {
            const targetId = typeof propertyId === 'string' ? parseInt(propertyId) : propertyId;
            setFocusedPropertyId(targetId);
            console.log('✅ 하이라이트 설정 완료 (재시도):', targetId);
            
            // 마커 찾기 시도
            let attempts = 0;
            const tryHighlight = () => {
              attempts++;
              const foundMarker = markersRef.current.find(({ property }) => {
                const propId = typeof property.id === 'string' ? parseInt(property.id) : property.id;
                return propId === targetId;
              });
              
              if (foundMarker && foundMarker.marker) {
                const pinElement = foundMarker.pinElement || foundMarker.marker.pinElement;
                
                if (pinElement) {
                  pinElement.style.transform = 'scale(2.5)';
                  pinElement.style.zIndex = '1000';
                  pinElement.style.borderWidth = '3px';
                  pinElement.style.borderColor = '#FF6B35';
                  pinElement.style.boxShadow = `
                    0 0 20px rgba(255, 107, 53, 0.8),
                    0 0 40px rgba(255, 107, 53, 0.6),
                    0 4px 8px rgba(0, 0, 0, 0.6),
                    inset 0 -2px 4px rgba(0, 0, 0, 0.2),
                    inset 0 1px 2px rgba(255, 255, 255, 0.8)
                  `;
                  pinElement.style.animation = 'pulse 1s ease-in-out infinite';
                  pinElement.style.transition = 'all 0.3s ease';
                  pinElement.classList.add('highlighted-pin');
                }
              } else if (attempts < 50) {
                setTimeout(tryHighlight, 100);
              }
            };
            setTimeout(tryHighlight, 200);
            
            setTimeout(() => {
              setFocusedPropertyId(null);
              // isManualFocus는 유지하여 사용자가 이동한 위치를 계속 유지
            }, 5000);
          }
        }
      }, 150);
      return;
    }
    
    const targetPosition = new window.kakao.maps.LatLng(numLat, numLng);
    console.log('지도 이동 시도:', targetPosition, '좌표:', { lat: numLat, lng: numLng, propertyId });
    
    // setCenter를 사용하여 즉시 이동
    map.current.setCenter(targetPosition);
    map.current.setLevel(1); // 최대 확대 (1이 최대, 숫자가 클수록 멀리)
    
    console.log('지도 이동 완료, 현재 중심:', map.current.getCenter().toString());
    
    // 하이라이트할 매물 ID 설정
    if (propertyId) {
      console.log('하이라이트 설정 예정:', propertyId);
      const targetId = typeof propertyId === 'string' ? parseInt(propertyId) : propertyId;
      // 즉시 하이라이트 ID 설정 (마커 생성 useEffect에서 확인하여 적용)
      setFocusedPropertyId(targetId);
      console.log('✅ 하이라이트 ID 설정 완료:', targetId);
      
      // 마커 생성이 완료될 때까지 기다린 후 하이라이트 재적용
      // 마커 생성은 비동기로 진행되므로 여러 번 시도
      let attempts = 0;
      const tryHighlight = () => {
        attempts++;
        const foundMarker = markersRef.current.find(({ property }) => {
          const propId = typeof property.id === 'string' ? parseInt(property.id) : property.id;
          return propId === targetId;
        });
        
        if (foundMarker && foundMarker.marker) {
          const pinElement = foundMarker.pinElement || foundMarker.marker.pinElement;
          
          if (pinElement) {
            console.log('✅ 마커 찾음, 하이라이트 적용:', targetId);
            pinElement.style.transform = 'scale(2.5)';
            pinElement.style.zIndex = '1000';
            pinElement.style.borderWidth = '3px';
            pinElement.style.borderColor = '#FF6B35';
            pinElement.style.boxShadow = `
              0 0 20px rgba(255, 107, 53, 0.8),
              0 0 40px rgba(255, 107, 53, 0.6),
              0 4px 8px rgba(0, 0, 0, 0.6),
              inset 0 -2px 4px rgba(0, 0, 0, 0.2),
              inset 0 1px 2px rgba(255, 255, 255, 0.8)
            `;
            pinElement.style.animation = 'pulse 1s ease-in-out infinite';
            pinElement.style.transition = 'all 0.3s ease';
            pinElement.classList.add('highlighted-pin');
          }
        } else if (attempts < 50) {
          // 최대 5초간 시도 (100ms * 50)
          setTimeout(tryHighlight, 100);
        } else {
          console.warn('⚠️ 마커를 찾지 못함 (최대 시도 횟수 초과):', targetId);
        }
      };
      setTimeout(tryHighlight, 200);
      
      // 5초 후 하이라이트 제거 (위치는 유지)
      setTimeout(() => {
        setFocusedPropertyId(null);
        console.log('하이라이트 제거 (위치 유지)');
        // isManualFocus는 유지하여 사용자가 이동한 위치를 계속 유지
      }, 5000);
    }
  };

  // 지도 초기 위치로 리셋
  const resetMap = () => {
    if (!map.current || !window.kakao?.maps) {
      return;
    }
    const centerPosition = new window.kakao.maps.LatLng(mapCenter.lat, mapCenter.lng);
    map.current.setCenter(centerPosition);
    map.current.setLevel(3); // 초기 줌 레벨
    // 사이드바 닫기
    setIsSidebarOpen(false);
    setSelectedProperty(null);
  };

  useImperativeHandle(ref, () => ({
    focusOnLocation,
    resetMap
  }));

  const filteredProperties = useMemo(
    () => applyFilters(propertyData, filters, consultations),
    [propertyData, filters, consultations]
  );

  useEffect(() => {
    if (map.current && typeof map.current.relayout === 'function') {
      map.current.relayout();
      // relayout 후 중심을 재설정하여 정확히 중앙에 위치하도록
      setTimeout(() => {
        if (map.current) {
          const currentCenter = new window.kakao.maps.LatLng(mapCenter.lat, mapCenter.lng);
          map.current.setCenter(currentCenter);
        }
      }, 50);
    }
  }, [availableHeight, mapCenter]);

  // 매물 데이터가 변경될 때마다 마커 업데이트
  useEffect(() => {
    if (!map.current) return;

    function createPropertyMarkers() {
      if (!map.current) return;

      // 기존 마커 제거
      markersRef.current.forEach(({ marker }) => {
        if (marker.setMap) {
          marker.setMap(null);
        } else if (marker.content) {
          // 커스텀 오버레이인 경우
          marker.setMap(null);
        }
      });
      markersRef.current = [];

      // 표시 여부 확인 및 권한 확인
      // GUEST는 마커를 표시하지 않고 상권별 개수만 표시
      if (!showMarkers || isVisitor || isGuest) return;

      // 모든 상권 목록 가져오기 (색상 할당용)
      const allAreas = [...new Set(filteredProperties.map(p => p.상권 || '기타'))].sort();

      // 매물 데이터로 마커 생성
      filteredProperties.forEach(property => {
        if (!property.lat || !property.lng) return; // 좌표가 없으면 스킵
        
        const position = new window.kakao.maps.LatLng(property.lat, property.lng);

        // 상권 정보 가져오기
        const areaName = property.상권 || '기타';
        const areaColor = getAreaColor(areaName, allAreas);
        const pinColor = areaColor.fill || areaColor.stroke || '#FF6B35'; // 기본 색상

        // property 객체를 클로저로 안전하게 캡처하기 위해 복사본 생성
        const propertyData = { ...property };
        
        // 클릭 이벤트 핸들러 정의
        const handleMarkerClick = () => {
          console.log('핀 클릭됨:', propertyData);
          setSelectedProperty(propertyData);
          setIsSidebarOpen(true);
          console.log('사이드바 열기 시도:', { property: propertyData, isOpen: true });
        };
        
        // 커스텀 원형 핀 생성 (클릭 핸들러 포함)
        const marker = createCustomPinMarker(map.current, position, pinColor, areaName, handleMarkerClick);
        if (!marker) return;

        // 마커와 property 정보를 함께 저장
        markersRef.current.push({ marker, property: propertyData, pinElement: marker.pinElement });
        
        // 마커 생성 직후 하이라이트 적용 (focusedPropertyId가 설정되어 있고, 이 매물이 대상인 경우)
        if (focusedPropertyId) {
          const targetId = typeof focusedPropertyId === 'string' ? parseInt(focusedPropertyId) : focusedPropertyId;
          const propertyId = typeof propertyData.id === 'string' ? parseInt(propertyData.id) : propertyData.id;
          
          // 마커 객체에 저장된 pinElement 사용
          const pinElement = marker.pinElement;
          
          if (propertyId === targetId && pinElement) {
            console.log('✅ 마커 생성 시 하이라이트 적용:', { propertyId, targetId });
            pinElement.style.transform = 'scale(2.5)';
            pinElement.style.zIndex = '1000';
            pinElement.style.borderWidth = '3px';
            pinElement.style.borderColor = '#FF6B35';
            pinElement.style.boxShadow = `
              0 0 20px rgba(255, 107, 53, 0.8),
              0 0 40px rgba(255, 107, 53, 0.6),
              0 4px 8px rgba(0, 0, 0, 0.6),
              inset 0 -2px 4px rgba(0, 0, 0, 0.2),
              inset 0 1px 2px rgba(255, 255, 255, 0.8)
            `;
            pinElement.style.animation = 'pulse 1s ease-in-out infinite';
            pinElement.style.transition = 'all 0.3s ease';
            pinElement.classList.add('highlighted-pin');
          }
        }
      });
      
      // 모든 마커 생성 완료 후 하이라이트 재적용 (혹시 놓친 경우 대비)
      if (focusedPropertyId) {
        setTimeout(() => {
          const targetId = typeof focusedPropertyId === 'string' ? parseInt(focusedPropertyId) : focusedPropertyId;
          markersRef.current.forEach(({ marker, property }) => {
            if (!marker) return;
            const pinElement = marker.pinElement;
            if (!pinElement) return;
            
            const propertyId = typeof property.id === 'string' ? parseInt(property.id) : property.id;
            if (propertyId === targetId) {
              pinElement.style.transform = 'scale(2.5)';
              pinElement.style.zIndex = '1000';
              pinElement.style.borderWidth = '3px';
              pinElement.style.borderColor = '#FF6B35';
              pinElement.style.boxShadow = `
                0 0 20px rgba(255, 107, 53, 0.8),
                0 0 40px rgba(255, 107, 53, 0.6),
                0 4px 8px rgba(0, 0, 0, 0.6),
                inset 0 -2px 4px rgba(0, 0, 0, 0.2),
                inset 0 1px 2px rgba(255, 255, 255, 0.8)
              `;
              pinElement.style.animation = 'pulse 1s ease-in-out infinite';
              pinElement.style.transition = 'all 0.3s ease';
              pinElement.classList.add('highlighted-pin');
              console.log('✅ 마커 생성 완료 후 하이라이트 재적용:', { propertyId, targetId });
            }
          });
        }, 100);
      }
    }

    createPropertyMarkers();
  }, [filteredProperties, showMarkers, isVisitor, isGuest, focusedPropertyId]); // focusedPropertyId도 dependency에 추가

  // 하이라이트 효과 적용
  useEffect(() => {
    console.log('하이라이트 useEffect 실행:', { 
      focusedPropertyId, 
      markersCount: markersRef.current.length,
      markers: markersRef.current.map(({ property }) => ({ id: property?.id, hasContent: !!markersRef.current[0]?.marker?.content }))
    });

    if (!focusedPropertyId) {
      // 하이라이트가 없으면 모든 마커를 일반 상태로 복원
      markersRef.current.forEach(({ marker, pinElement: storedPinElement }) => {
        if (!marker) return;
        
        // 저장된 pinElement 사용 (없으면 마커 객체에서 가져오기)
        const pinElement = storedPinElement || marker.pinElement;
        if (!pinElement) return;
        pinElement.style.transform = 'scale(1)';
        pinElement.style.zIndex = '1';
        pinElement.style.borderWidth = '2px';
        pinElement.style.borderColor = 'rgba(0, 0, 0, 0.6)';
        pinElement.style.boxShadow = `
          0 2px 4px rgba(0, 0, 0, 0.4),
          inset 0 -2px 4px rgba(0, 0, 0, 0.2),
          inset 0 1px 2px rgba(255, 255, 255, 0.5)
        `;
        pinElement.style.animation = 'none';
      });
      return;
    }

    // ID 비교를 위해 타입 통일 (숫자로 변환)
    const targetId = typeof focusedPropertyId === 'string' ? parseInt(focusedPropertyId) : focusedPropertyId;
    console.log('하이라이트 대상 ID:', targetId, '마커 개수:', markersRef.current.length);
    
    let found = false;
    markersRef.current.forEach(({ marker, property, pinElement: storedPinElement }) => {
      if (!marker) {
        console.log('마커 없음:', property?.id);
        return;
      }
      
      // 저장된 pinElement 사용 (카카오맵 예제처럼 마커 객체에 직접 저장된 참조)
      const pinElement = storedPinElement || marker.pinElement;
      
      if (!pinElement) {
        console.log('마커 pinElement 없음:', property?.id, marker);
        return;
      }
      // property.id도 숫자로 변환하여 비교
      const propertyId = typeof property.id === 'string' ? parseInt(property.id) : property.id;
      const isFocused = propertyId === targetId;
      
      console.log('마커 체크:', { propertyId, targetId, isFocused, pinElement: !!pinElement });
      
      if (isFocused) {
        found = true;
        console.log('✅ 하이라이트 적용 시작:', { propertyId, targetId, pinElement });
        
        // 하이라이트 효과: 크기 확대, 강한 그림자, 펄스 애니메이션
        try {
          pinElement.style.transform = 'scale(2.5)';
          pinElement.style.zIndex = '1000';
          pinElement.style.borderWidth = '3px';
          pinElement.style.borderColor = '#FF6B35';
          pinElement.style.boxShadow = `
            0 0 20px rgba(255, 107, 53, 0.8),
            0 0 40px rgba(255, 107, 53, 0.6),
            0 4px 8px rgba(0, 0, 0, 0.6),
            inset 0 -2px 4px rgba(0, 0, 0, 0.2),
            inset 0 1px 2px rgba(255, 255, 255, 0.8)
          `;
          pinElement.style.animation = 'pulse 1s ease-in-out infinite';
          pinElement.style.transition = 'all 0.3s ease';
          
          // 추가로 클래스를 추가하여 더 명확하게 표시
          pinElement.classList.add('highlighted-pin');
          
          console.log('✅ 하이라이트 스타일 적용 완료');
        } catch (error) {
          console.error('하이라이트 스타일 적용 실패:', error);
        }
      } else {
        // 일반 상태로 복원
        pinElement.style.transform = 'scale(1)';
        pinElement.style.zIndex = '1';
        pinElement.style.borderWidth = '2px';
        pinElement.style.borderColor = 'rgba(0, 0, 0, 0.6)';
        pinElement.style.boxShadow = `
          0 2px 4px rgba(0, 0, 0, 0.4),
          inset 0 -2px 4px rgba(0, 0, 0, 0.2),
          inset 0 1px 2px rgba(255, 255, 255, 0.5)
        `;
        pinElement.style.animation = 'none';
        pinElement.classList.remove('highlighted-pin');
      }
    });
    
    if (!found && focusedPropertyId) {
      const allIds = markersRef.current.map(({ property }) => property?.id).sort((a, b) => {
        const idA = typeof a === 'string' ? parseInt(a) : a;
        const idB = typeof b === 'string' ? parseInt(b) : b;
        return idA - idB;
      });
      console.warn('⚠️ 하이라이트 대상 매물을 찾지 못함:', targetId, '마커 목록 (정렬):', allIds.slice(0, 20), '...', '전체 개수:', allIds.length);
      console.warn('⚠️ 필터링된 매물에 해당 ID가 포함되어 있는지 확인 필요');
    }
  }, [focusedPropertyId, filteredProperties]); // filteredProperties도 dependency에 추가하여 마커가 생성된 후에도 하이라이트 적용


  // 다각형의 중심점 계산 함수
  const getPolygonCenter = (points) => {
    if (points.length === 0) return null;
    const sumLat = points.reduce((sum, p) => sum + p.lat, 0);
    const sumLng = points.reduce((sum, p) => sum + p.lng, 0);
    return {
      lat: sumLat / points.length,
      lng: sumLng / points.length
    };
  };

  // 하드코딩된 다각형 표시
  useEffect(() => {
    // 지도가 준비될 때까지 기다림
    if (!map.current || !window.kakao?.maps) {
      // 지도가 아직 준비되지 않았으면 잠시 후 다시 시도
      const timer = setTimeout(() => {
        if (map.current && window.kakao?.maps && showPolygons && customPolygons.length > 0) {
          // 재귀적으로 다시 시도 (하지만 무한 루프 방지를 위해 한 번만)
          const allAreas = [...new Set([
            ...customPolygons.map(p => p.name),
            ...propertyData.map(p => p.상권 || '기타')
          ])].sort();

          customPolygons.forEach((customPolygon) => {
            if (customPolygon.points.length < 3) return;

            const path = customPolygon.points.map(point => 
              new window.kakao.maps.LatLng(point.lat, point.lng)
            );

            const areaColor = getAreaColor(customPolygon.name, allAreas);
            const polygonColor = areaColor.fill || areaColor.stroke || customPolygon.color || '#FF4500';

            const polygon = new window.kakao.maps.Polygon({
              map: map.current,
              path: path,
              strokeWeight: 4,
              strokeColor: polygonColor,
              strokeOpacity: 1.0,
              strokeStyle: 'solid',
              fillColor: polygonColor,
              fillOpacity: 0.3,
              zIndex: 1
            });

            customPolygonPolygonsRef.current.push(polygon);

            const center = getPolygonCenter(customPolygon.points);
            if (center) {
              // GUEST일 때는 상권별 매물 개수 표시
              let labelText = customPolygon.name;
              if (isGuest) {
                // 필터링된 매물 중에서 해당 상권의 매물 개수 계산
                const areaProperties = filteredProperties.filter(p => (p.상권 || '기타') === customPolygon.name);
                const count = areaProperties.length;
                labelText = `${customPolygon.name} (${count}개)`;
              }
              
              const labelContent = `
                <div style="
                  font-size: 18px;
                  font-weight: bold;
                  color: ${polygonColor};
                  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
                  white-space: nowrap;
                  background-color: rgba(255,255,255,0.9);
                  padding: 4px 8px;
                  border-radius: 4px;
                  border: 2px solid ${polygonColor};
                ">${labelText}</div>
              `;

              const label = new window.kakao.maps.CustomOverlay({
                position: new window.kakao.maps.LatLng(center.lat, center.lng),
                content: labelContent,
                yAnchor: 0.5,
                xAnchor: 0.5,
                zIndex: 10
              });

              label.setMap(map.current);
              customPolygonLabelsRef.current.push(label);
            }
          });
        }
      }, 500);
      return () => clearTimeout(timer);
    }

    // 기존 커스텀 다각형 및 라벨 제거
    customPolygonPolygonsRef.current.forEach(polygon => polygon.setMap(null));
    customPolygonPolygonsRef.current = [];
    customPolygonLabelsRef.current.forEach(label => label.setMap(null));
    customPolygonLabelsRef.current = [];

    if (showPolygons && customPolygons.length > 0) {
      // 모든 상권 목록 가져오기 (색상 할당 일관성 유지)
      // GUEST일 때는 필터링된 매물 기준으로 개수 계산
      const allAreas = [...new Set([
        ...customPolygons.map(p => p.name),
        ...(isGuest ? filteredProperties : propertyData).map(p => p.상권 || '기타')
      ])].sort();

      customPolygons.forEach((customPolygon, index) => {
        if (customPolygon.points.length < 3) return;

        const path = customPolygon.points.map(point => 
          new window.kakao.maps.LatLng(point.lat, point.lng)
        );

        // 상권명을 기반으로 색상 가져오기 (매물 핀과 동일한 로직)
        const areaColor = getAreaColor(customPolygon.name, allAreas);
        const polygonColor = areaColor.fill || areaColor.stroke || customPolygon.color || '#FF4500';

        // 다각형 그리기
        const polygon = new window.kakao.maps.Polygon({
          map: map.current,
          path: path,
          strokeWeight: 4,
          strokeColor: polygonColor,
          strokeOpacity: 1.0,
          strokeStyle: 'solid',
          fillColor: polygonColor,
          fillOpacity: 0.3,
          zIndex: 1
        });

        customPolygonPolygonsRef.current.push(polygon);

        // 다각형 중심에 라벨 추가
        const center = getPolygonCenter(customPolygon.points);
        if (center) {
          // GUEST일 때는 상권별 매물 개수 표시
          let labelText = customPolygon.name;
          if (isGuest) {
            // 필터링된 매물 중에서 해당 상권의 매물 개수 계산
            const areaProperties = filteredProperties.filter(p => (p.상권 || '기타') === customPolygon.name);
            const count = areaProperties.length;
            labelText = `${customPolygon.name} (${count}개)`;
          }
          
          const labelContent = `
            <div style="
              font-size: 18px;
              font-weight: bold;
              color: ${polygonColor};
              text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
              white-space: nowrap;
              background-color: rgba(255,255,255,0.9);
              padding: 4px 8px;
              border-radius: 4px;
              border: 2px solid ${polygonColor};
            ">${labelText}</div>
          `;

          const label = new window.kakao.maps.CustomOverlay({
            position: new window.kakao.maps.LatLng(center.lat, center.lng),
            content: labelContent,
            yAnchor: 0.5,
            xAnchor: 0.5,
            zIndex: 10
          });

          label.setMap(map.current);
          customPolygonLabelsRef.current.push(label);
        }
      });
    }

    return () => {
      customPolygonPolygonsRef.current.forEach(polygon => polygon.setMap(null));
      customPolygonLabelsRef.current.forEach(label => label.setMap(null));
    };
  }, [customPolygons, showPolygons, propertyData, filteredProperties, isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

  const containerStyle = availableHeight
    ? { 
        height: `${availableHeight}px`, 
        minHeight: `${MIN_MAP_HEIGHT}px`,
        maxHeight: `${availableHeight}px`,
        overflow: 'hidden'
      }
    : { 
        minHeight: `${MIN_MAP_HEIGHT}px`,
        overflow: 'hidden'
      };

  const handleLocalFocus = (property) => {
    if (!property?.lat || !property?.lng) return;
    focusOnLocation(Number(property.lat), Number(property.lng), property.id);
  };

  // 매물 삭제 핸들러
  const handleDeleteProperty = async (propertyId) => {
    try {
      await deleteProperty(propertyId);
      // 데이터 다시 불러오기 (지도 표시용이므로 좌표가 있는 매물만)
      const data = await fetchProperties(role, true); // hasCoordinates=true
      setPropertyData(data);
      // 사이드바 닫기
      setIsSidebarOpen(false);
      setSelectedProperty(null);
      alert('매물이 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('매물 삭제 실패:', error);
      throw error;
    }
  };

  // 매물 업데이트 후 데이터 새로고침
  const handlePropertyUpdate = async () => {
    try {
      // 지도 표시용이므로 좌표가 있는 매물만
      const data = await fetchProperties(role, true); // hasCoordinates=true
      setPropertyData(data);
    } catch (error) {
      console.error('매물 데이터 새로고침 실패:', error);
    }
  };

  // 스크린샷 찍기 함수
  const handleTakeScreenshot = useCallback(async () => {
    if (!mapContainer.current) return;

    try {
      // 지도 컨테이너만 캡처
      const canvas = await html2canvas(mapContainer.current, {
        backgroundColor: '#ffffff',
        useCORS: true,
        scale: 2, // 고해상도
        logging: false
      });

      // 16:9 비율로 리사이즈
      const targetWidth = 1920; // 16:9 기준 너비
      const targetHeight = 1080; // 16:9 기준 높이
      
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = targetWidth;
      resizedCanvas.height = targetHeight;
      const ctx = resizedCanvas.getContext('2d');
      
      // 원본 비율 유지하면서 16:9로 맞추기
      const sourceAspect = canvas.width / canvas.height;
      const targetAspect = targetWidth / targetHeight;
      
      let sx = 0, sy = 0, sw = canvas.width, sh = canvas.height;
      
      if (sourceAspect > targetAspect) {
        // 원본이 더 넓음 - 높이 기준으로 자르기
        sw = canvas.height * targetAspect;
        sx = (canvas.width - sw) / 2;
      } else {
        // 원본이 더 높음 - 너비 기준으로 자르기
        sh = canvas.width / targetAspect;
        sy = (canvas.height - sh) / 2;
      }
      
      ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
      
      // 다운로드
      resizedCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `지도_스크린샷_${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('스크린샷 생성 실패:', error);
      alert('스크린샷 생성에 실패했습니다.');
    }
  }, []);

  // 스크린샷 모드일 때는 다른 UI 숨기기
  if (isScreenshotMode) {
    return (
      <div
        className="w-full h-full relative"
        style={{ width: '100vw', height: '100vh' }}
      >
        <div 
          ref={mapContainer} 
          className="w-full h-full"
          style={{ touchAction: 'pan-x pan-y' }}
        ></div>
        
        {/* 스크린샷 모드 컨트롤 */}
        <div className="absolute top-4 right-4 z-30 flex gap-2">
          <button
            onClick={handleTakeScreenshot}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            스크린샷 저장 (16:9)
          </button>
          <button
            onClick={onExitScreenshotMode}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            종료 (ESC)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full relative border border-gray-200 rounded-2xl shadow-sm bg-white"
      style={containerStyle}
    >
      <div 
        ref={mapContainer} 
        className="w-full h-full"
        style={{ 
          touchAction: 'pan-x pan-y',
          height: '100%',
          overflow: 'hidden'
        }}
      ></div>
      
      {/* 로딩 표시 */}
      {isLoading && (
        <div className="absolute top-2 md:top-4 left-1/2 transform -translate-x-1/2 bg-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg shadow-lg z-10">
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-orange-500"></div>
            <span className="text-xs md:text-sm text-gray-700">매물 데이터를 불러오는 중...</span>
          </div>
        </div>
      )}

      {/* 에러 표시 */}
      {error && !isLoading && (
        <div className="absolute top-2 md:top-4 left-1/2 transform -translate-x-1/2 bg-red-50 border border-red-200 px-3 md:px-4 py-1.5 md:py-2 rounded-lg shadow-lg z-10 max-w-[90%] md:max-w-md">
          <div className="flex items-center gap-1.5 md:gap-2">
            <span className="text-red-600 text-xs md:text-sm">⚠️ {error}</span>
          </div>
        </div>
      )}

      {/* 좌표 없는 매물 경고 아이콘 */}
      {!isScreenshotMode && propertiesWithoutCoordinates.length > 0 && (
        <div className="absolute top-2 md:top-4 right-2 md:right-4 z-30">
          <button
            onClick={() => setIsWarningModalOpen(true)}
            className="bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg p-2 md:p-3 transition-colors relative"
            aria-label="좌표 없는 매물 경고"
            title={`좌표가 없는 매물 ${propertiesWithoutCoordinates.length}개 발견`}
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {propertiesWithoutCoordinates.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-yellow-400 text-red-800 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {propertiesWithoutCoordinates.length > 9 ? '9+' : propertiesWithoutCoordinates.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* 퀵메뉴 */}
      <div className={`absolute top-2 md:top-4 right-2 md:right-4 z-20 flex flex-col gap-1.5 md:gap-2 ${propertiesWithoutCoordinates.length > 0 ? 'mt-12 md:mt-14' : ''}`}>
        {/* 퀵메뉴 토글 버튼 */}
        <button
          onClick={() => setIsQuickMenuOpen(!isQuickMenuOpen)}
          className="bg-white rounded-lg shadow-lg p-2 md:p-3 hover:bg-gray-50 transition-colors"
          aria-label="퀵메뉴"
        >
          <svg 
            className={`w-5 h-5 md:w-6 md:h-6 text-gray-700 transition-transform ${isQuickMenuOpen ? 'rotate-90' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* 퀵메뉴 옵션 */}
        {isQuickMenuOpen && (
          <div className="bg-white rounded-lg shadow-lg p-1.5 md:p-2 flex flex-col gap-1.5 md:gap-2 min-w-[120px] md:min-w-[140px]">
            {/* 매물 핀 토글 */}
            <button
              onClick={() => setShowMarkers(!showMarkers)}
              disabled={isVisitor}
              className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-colors ${
                isVisitor ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                showMarkers 
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>매물 핀</span>
              {showMarkers && (
                <svg className="w-3 h-3 md:w-4 md:h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* 다각형 토글 */}
            <button
              onClick={() => setShowPolygons(!showPolygons)}
              className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-colors ${
                showPolygons 
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span>다각형</span>
              {showPolygons && (
                <svg className="w-3 h-3 md:w-4 md:h-4 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* 스크린샷 모드 버튼 */}
            {onEnterScreenshotMode && (
              <button
                onClick={onEnterScreenshotMode}
                className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded text-xs md:text-sm font-medium transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>스크린샷 모드</span>
              </button>
            )}

          </div>
        )}
      </div>
      
      {/* 사이드바 */}
      <PropertySidebar
        property={selectedProperty}
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedProperty(null);
        }}
        onFocusMap={onFocusMap}
        onLocalFocus={handleLocalFocus}
        onAddInterestedProperty={onAddInterestedProperty}
        onDelete={handleDeleteProperty}
        onPropertyUpdate={handlePropertyUpdate}
      />

      {/* 좌표 없는 매물 경고 모달 */}
      {isWarningModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setIsWarningModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div className="bg-red-500 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-lg font-bold">좌표가 없는 매물 ({propertiesWithoutCoordinates.length}개)</h2>
              </div>
              <button
                onClick={() => setIsWarningModalOpen(false)}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="닫기"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 모달 본문 */}
            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600 mb-4">
                다음 매물들은 주소는 있지만 지오코딩이 실패하여 지도에 표시되지 않습니다. 목록 뷰에서 주소를 수정하거나 다시 지오코딩을 시도해주세요.
              </p>
              <div className="space-y-2">
                {propertiesWithoutCoordinates.map((property) => (
                  <div
                    key={property.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setIsWarningModalOpen(false);
                      if (onSwitchToTab) {
                        onSwitchToTab('list');
                        // 목록 뷰에서 해당 매물로 스크롤하거나 선택할 수 있도록 이벤트 발생
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('focusProperty', { detail: { propertyId: property.id } }));
                        }, 100);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{property.상호 || property.name || '이름 없음'}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          <div>주소: {property.주소 || '주소 없음'}</div>
                          {property.상권 && <div>상권: {property.상권}</div>}
                          {property['파일명'] && <div className="text-xs text-gray-500 mt-1">파일: {property['파일명']}</div>}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 모달 푸터 */}
            <div className="bg-gray-50 px-4 py-3 flex justify-end gap-2">
              <button
                onClick={() => setIsWarningModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                닫기
              </button>
              {onSwitchToTab && (
                <button
                  onClick={() => {
                    setIsWarningModalOpen(false);
                    onSwitchToTab('list');
                  }}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  목록 뷰로 이동
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default MainMap;
